import { c as _c } from "react/compiler-runtime";
import { motion } from "framer-motion";
import { Icon } from "../icons";
import { FFLogo } from "./FFLogo";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Default social platforms and copyright
const DEFAULT_SOCIAL_PLATFORMS = [{
  name: "Facebook",
  icon: "Facebook",
  href: "https://www.facebook.com/fasterforward.se"
}, {
  name: "Instagram",
  icon: "Instagram",
  href: "https://www.instagram.com/fasterforward"
}, {
  name: "LinkedIn",
  icon: "LinkedIn",
  href: "https://www.linkedin.com/company/fasterforward/"
}];
const getCopyrightText = () => `© ${new Date().getFullYear()} FasterForward AB. Alla rättigheter förbehållna.`;
const COMPANY_DESCRIPTION = "Faster Forward är en plats för nöje, passion och upptäckarglädje";

// Animation variants
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.3
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 10
    }
  }
};
const linkHoverVariants = {
  initial: {
    x: 0
  },
  hover: {
    x: 3,
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 20
    }
  }
};

// Utility function
function scrollToSection(e, id) {
  e.preventDefault();
  const element = document.querySelector(id);
  if (element) {
    element.scrollIntoView({
      behavior: "smooth"
    });
  }
}

// Logo Component
function FooterLogo(t0) {
  const $ = _c(10);
  const {
    theme
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      scale: 1.05
    };
    t2 = {
      duration: 0.3
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  const t3 = theme?.logoVariant || "full";
  const t4 = theme?.logoClassName;
  let t5;
  if ($[2] !== t3 || $[3] !== t4) {
    t5 = /*#__PURE__*/_jsx("a", {
      href: "/",
      children: /*#__PURE__*/_jsx(motion.div, {
        whileHover: t1,
        transition: t2,
        children: /*#__PURE__*/_jsx("div", {
          className: "relative w-40 h-14",
          children: /*#__PURE__*/_jsx(FFLogo, {
            variant: t3,
            className: t4
          })
        })
      })
    });
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const t6 = `mt-4 ${theme?.textColor || "text-gray-600"}`;
  let t7;
  if ($[5] !== t6) {
    t7 = /*#__PURE__*/_jsx("p", {
      className: t6,
      children: COMPANY_DESCRIPTION
    });
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== t5 || $[8] !== t7) {
    t8 = /*#__PURE__*/_jsxs(motion.div, {
      variants: itemVariants,
      className: "max-w-sm",
      children: [t5, t7]
    });
    $[7] = t5;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  return t8;
}

// Links Section Component
function LinksSection(t0) {
  const $ = _c(13);
  const {
    title,
    links,
    theme
  } = t0;
  if (!links.length) {
    return null;
  }
  let t1;
  if ($[0] !== theme?.textColor || $[1] !== title) {
    t1 = title && /*#__PURE__*/_jsx("h3", {
      className: `font-semibold mb-6 ${theme?.textColor || "text-gray-600"}`,
      children: title
    });
    $[0] = theme?.textColor;
    $[1] = title;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== links || $[4] !== theme) {
    let t3;
    if ($[6] !== theme) {
      t3 = item => /*#__PURE__*/_jsx(FooterLink, {
        item: item,
        theme: theme
      }, item.name);
      $[6] = theme;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    t2 = links.map(t3);
    $[3] = links;
    $[4] = theme;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[8] !== t2) {
    t3 = /*#__PURE__*/_jsx("ul", {
      className: "space-y-4",
      children: t2
    });
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t1 || $[11] !== t3) {
    t4 = /*#__PURE__*/_jsxs(motion.div, {
      variants: itemVariants,
      children: [t1, t3]
    });
    $[10] = t1;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}

// Social Media Component
function SocialMedia(t0) {
  const $ = _c(10);
  const {
    title,
    theme
  } = t0;
  let t1;
  if ($[0] !== theme?.textColor || $[1] !== title) {
    t1 = title && /*#__PURE__*/_jsx("h3", {
      className: `font-semibold mb-6 ${theme?.textColor || "text-gray-600"}`,
      children: title
    });
    $[0] = theme?.textColor;
    $[1] = title;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== theme) {
    t2 = DEFAULT_SOCIAL_PLATFORMS.map(platform => /*#__PURE__*/_jsx(SocialLink, {
      platform: platform,
      theme: theme
    }, platform.name));
    $[3] = theme;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = /*#__PURE__*/_jsx("div", {
      className: "flex flex-col space-y-4",
      children: t2
    });
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t3) {
    t4 = /*#__PURE__*/_jsxs(motion.div, {
      variants: itemVariants,
      children: [t1, t3]
    });
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}

// Footer Link Component
function FooterLink(t0) {
  const $ = _c(11);
  const {
    item,
    theme
  } = t0;
  const linkClass = `${theme?.textColor || "text-gray-600"} hover:${theme?.hoverColor || "text-[#ff4d4d]"} transition-colors duration-300 block`;
  if (item.id) {
    let t1;
    if ($[0] !== item.id) {
      t1 = e => scrollToSection(e, item.id);
      $[0] = item.id;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== item.id || $[3] !== item.name || $[4] !== linkClass || $[5] !== t1) {
      t2 = /*#__PURE__*/_jsx(motion.li, {
        initial: "initial",
        whileHover: "hover",
        variants: linkHoverVariants,
        children: /*#__PURE__*/_jsx("a", {
          href: item.id,
          onClick: t1,
          className: linkClass,
          children: item.name
        })
      });
      $[2] = item.id;
      $[3] = item.name;
      $[4] = linkClass;
      $[5] = t1;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    return t2;
  }
  const t1 = item.href || "/";
  let t2;
  if ($[7] !== item.name || $[8] !== linkClass || $[9] !== t1) {
    t2 = /*#__PURE__*/_jsx(motion.li, {
      initial: "initial",
      whileHover: "hover",
      variants: linkHoverVariants,
      children: /*#__PURE__*/_jsx("a", {
        href: t1,
        className: linkClass,
        children: item.name
      })
    });
    $[7] = item.name;
    $[8] = linkClass;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}

// Social Link Component
function SocialLink(t0) {
  const $ = _c(9);
  const {
    platform,
    theme
  } = t0;
  const iconClass = `w-6 h-6 ${theme?.textColor || "text-gray-600"} group-hover:${theme?.hoverColor || "text-[#ff4d4d]"} transition-colors duration-300`;
  const t1 = `${theme?.textColor || "text-gray-600"} hover:${theme?.hoverColor || "text-[#ff4d4d]"} transition-colors duration-300 flex items-center gap-2 group`;
  const t2 = `Besök vår ${platform.name}-sida`;
  let t3;
  if ($[0] !== iconClass || $[1] !== platform.icon) {
    t3 = /*#__PURE__*/_jsx(Icon, {
      name: platform.icon,
      className: iconClass
    });
    $[0] = iconClass;
    $[1] = platform.icon;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== platform.href || $[4] !== platform.name || $[5] !== t1 || $[6] !== t2 || $[7] !== t3) {
    t4 = /*#__PURE__*/_jsx(motion.div, {
      initial: "initial",
      whileHover: "hover",
      variants: linkHoverVariants,
      children: /*#__PURE__*/_jsxs("a", {
        href: platform.href,
        className: t1,
        "aria-label": t2,
        target: "_blank",
        rel: "noopener noreferrer",
        children: [t3, platform.name]
      })
    });
    $[3] = platform.href;
    $[4] = platform.name;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}

// Copyright Component
function Copyright(t0) {
  const $ = _c(3);
  const {
    theme
  } = t0;
  const t1 = `pt-8 text-center ${theme?.textColor || "text-gray-600"}`;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = /*#__PURE__*/_jsx("p", {
      children: getCopyrightText()
    });
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== t1) {
    t3 = /*#__PURE__*/_jsx(motion.div, {
      variants: itemVariants,
      className: t1,
      children: t2
    });
    $[1] = t1;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  return t3;
}
// Main Footer Component
export function Footer(t0) {
  const $ = _c(24);
  const {
    quickLinks,
    services,
    className: t1,
    theme
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const t2 = `${theme?.bgColor || "bg-white"} pt-24 pb-12 ${className}`;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      once: true,
      margin: "-100px"
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] !== theme) {
    t4 = /*#__PURE__*/_jsx(FooterLogo, {
      theme: theme
    });
    $[1] = theme;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== quickLinks || $[4] !== theme) {
    t5 = quickLinks && /*#__PURE__*/_jsx(LinksSection, {
      title: quickLinks.title,
      links: quickLinks.links,
      theme: theme
    });
    $[3] = quickLinks;
    $[4] = theme;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== services || $[7] !== theme) {
    t6 = services && /*#__PURE__*/_jsx(LinksSection, {
      title: services.title,
      links: services.links,
      theme: theme
    });
    $[6] = services;
    $[7] = theme;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== theme) {
    t7 = /*#__PURE__*/_jsx(SocialMedia, {
      title: "F\xF6lj oss",
      theme: theme
    });
    $[9] = theme;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t4 || $[12] !== t5 || $[13] !== t6 || $[14] !== t7) {
    t8 = /*#__PURE__*/_jsxs("div", {
      className: "grid grid-cols-1 md:grid-cols-4 gap-12 mb-16",
      children: [t4, t5, t6, t7]
    });
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== theme) {
    t9 = /*#__PURE__*/_jsx(Copyright, {
      theme: theme
    });
    $[16] = theme;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t8 || $[19] !== t9) {
    t10 = /*#__PURE__*/_jsxs(motion.div, {
      className: "container mx-auto px-4",
      variants: containerVariants,
      initial: "hidden",
      whileInView: "visible",
      viewport: t3,
      children: [t8, t9]
    });
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t2) {
    t11 = /*#__PURE__*/_jsx("footer", {
      className: t2,
      children: t10
    });
    $[21] = t10;
    $[22] = t2;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  return t11;
}